import { createSelector, createSlice } from "@reduxjs/toolkit";
import { InternalReportType } from "../../../api/tenantInternalReports";
import { TenantFeatureFlagKey } from "../../../../src/api/featureFlag";
import { OptionValues } from "../../../api/common";
import { ProductOption } from "../../../api/ProductOptions";
import { RootState } from "../../../app/store";
import {
  fetchFeatureFlags,
  fetchProductOptions,
  fetchTenantOptions,
} from "./Thunks";
import { IOAppointingAuthority } from "../../../api/observationByAuthority";
import { FraudElementContributors } from "../../../api/fraudElements";

interface ConfigsState {
  tenantOptions: OptionValues[];
  productOptions: ProductOption[];
  featureFlags: Record<string, string>;
}

const initialState: ConfigsState = {
  tenantOptions: [],
  productOptions: [],
  featureFlags: {},
};

const configsSlice = createSlice({
  name: "configs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchTenantOptions.fulfilled, (state, action) => {
      state.tenantOptions = action.payload;
    });
    builder.addCase(fetchProductOptions.fulfilled, (state, action) => {
      state.productOptions = action.payload;
    });
    builder.addCase(fetchFeatureFlags.fulfilled, (state, action) => {
      const flagsMap: Record<string, string> = {};

      action.payload.forEach((flag) => {
        flagsMap[flag.ff_key] = flag.ff_value;
      });

      state.featureFlags = flagsMap;
    });   
  },
});

export const getZoneValues = createSelector(
  [(state: RootState) => state.configs.tenantOptions],
  (tenantOptions) =>
    tenantOptions.find((optionValue) => optionValue.name === "zones")?.values || []
);
export const getRegionValues = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "regions"
  )?.values || [];
export const getBranchValues = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "branches"
  )?.values || [];
export const getSourceValues = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "sources"
  )?.values || [];
export const getPartyTypes = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "party_types"
  )?.values || [];
export const getInvestigationNatures = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "investigation_natures"
  )?.values || [];
export const getProducts = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "products"
  )?.values || [];
export const getCompetentAuthorities = (state: RootState) =>
  state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "io_appointing_authorities",
  )?.values || [
    IOAppointingAuthority.ZFRC,
    IOAppointingAuthority.ZONAL_INSPECTORATE,
    IOAppointingAuthority.HEAD_OF_INTERNAL_AUDIT
  ];  


export const getInternalReportsTypes = (
  state: RootState
): InternalReportType[] =>
  (state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "internal_reports"
  )?.values ?? []) as InternalReportType[];

export const getProductOptions = (state: RootState) => state.configs.productOptions;

export const isPreviewFeatureEnabled =
  (featureName: string) =>
  (state: RootState): boolean =>
    ((
      state.configs.tenantOptions.find(
        (optionValue) => optionValue.name === "preview_features"
      )?.values ?? []
    ) as string[]).includes(featureName);

export const getFeatureFlags = (state: RootState) =>
  state.configs.featureFlags;

export const isAIEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[TenantFeatureFlagKey.AI_FEATURES_ENABLED] === "Y";

export const isBoardroomPresentationEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[ TenantFeatureFlagKey.BOARD_PRESENTATION_ENABLED] === "Y";

export const isDocumentsPanelEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.DOCUMENTS_PANEL_ENABLED
  ] === "Y";

export const isAIInsightsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.AI_INSIGHTS_ENABLED
  ] === "Y";  

export const isFinalFICV2Enabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.FINAL_FIC_V2_ENABLED
  ] === "Y";  

export const isReportsPolicyNormalizationEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.REPORTS_POLICY_NORMALIZATION_ENABLED
  ] === "Y";

export const isInternalReportsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[TenantFeatureFlagKey.INTERNAL_REPORTS_ENABLED] === "Y";

export const isIdentityProfileVerificationsV2Enabled = (
  state: RootState,
): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.IDENTITY_PROFILE_VERIFICATIONS_V2_ENABLED
  ] === "Y";

export const isLegalTechnicalAnalysisV2Enabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.LEGAL_TECHNICAL_ANALYSIS_V2_ENABLED
  ] === "Y";  

export const isSanctionsAndDisbursementEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.SANCTION_AND_DISBURSEMENT_ENABLED
  ] === "Y";


export const isFraudElementsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.FRAUD_ELEMENTS_ENABLED
  ] === "Y";

export const isGenerateFraudElementsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.GENERATE_FRAUD_ELEMENTS_ENABLED
  ] === "Y";

export const isCreditAccountStakeholdersRevampEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.CREDIT_ACCOUNT_STAKEHOLDERS_REVAMP_ENABLED
  ] === "Y";

export const isOperaionalCyberRevampEnabled = (state: RootState): boolean => {
  return state.configs.featureFlags[
    TenantFeatureFlagKey.OPERATIONAL_CYBER_REVAMP_ENABLED
  ] === "Y";
}

export const isAccountDetailsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.ACCOUNT_DETAILS_ENABLED
  ] === "Y";
  
export const isStaticDashboardDemoEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.STATIC_DASHBOARD_DEMO_ENABLED
  ] === "Y";

export const isFICIncidentParticularsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.FIC_INCEDENT_PARTICULARS_ENABLED
  ] === "Y"; 

export const isInvestigationReportDownloadEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.INVESTIGATION_REPORT_DOWNLOAD_ENABLED
  ] === "Y";  

export const isInspectionsAndAuditsEnabled = (state: RootState): boolean =>
  state.configs.featureFlags[
    TenantFeatureFlagKey.INSPECTIONS_AND_AUDITS_ENABLED
  ] === "Y";  

export const getPlannedMaintenanceDates = (state: RootState): { startDate: Date; endDate: Date } | null => {
  const value =state.configs.featureFlags[TenantFeatureFlagKey.PLANNED_MAINTAINANCE_DATES];

  if (!value) return null;

  const [startDateString, endDateString] = value.split("-").map((v) => v.trim());

  if (!startDateString || !endDateString) return null;

  const parseDate = (dateString: string): Date | null => {
    const [day, month, year] = dateString.split("/").map(Number);

    if (!day || !month || !year) return null;

    return new Date(year, month - 1, day);
  };

  const startDate = parseDate(startDateString);
  const endDate = parseDate(endDateString);

  if (!startDate || !endDate) return null;

  return { startDate, endDate };
};

export const getReferralDateMaxLookbackDays = (
  state: RootState
): number | null => {
  const value =
    state.configs.featureFlags[
      TenantFeatureFlagKey.REFERRAL_DATE_MAX_LOOKBACK_DAYS
    ];

  return value === undefined ? null : parseInt(value, 10);
};

export const getFraudElementContributors = (state: RootState) =>
  (state.configs.tenantOptions.find(
    (optionValue) => optionValue.name === "fraud_element_contributors",
  )?.values ?? []) as FraudElementContributors[];

export const configsReducer = configsSlice.reducer;